import { inject, Injectable } from '@angular/core';
import { SharedHttpClient } from '@luggagehero/shared/services/http';
import { SharedStorageService } from '@luggagehero/shared/services/storage';
import { forkJoin, map, Observable, of } from 'rxjs';

import { TRANSLATE_CONFIG, TranslationMap } from './+state';

/**
 * A service for communicating with the translation API
 */
@Injectable({
  providedIn: 'root',
})
export class SharedTranslationsService {
  private config = inject(TRANSLATE_CONFIG);
  private storage = inject(SharedStorageService);
  private http = inject(SharedHttpClient);

  private excludedAppTranslationKeys: Set<string> = new Set();

  /** The configured app names as an array (a single-string config becomes a one-element list). */
  private get appNames(): readonly string[] {
    const appName = this.config.appName;
    return typeof appName === 'string' ? [appName] : appName;
  }

  /**
   * Stable local key under which registered-key bookkeeping is stored. A single-app config uses the
   * bare app name (unchanged); a multi-app config joins with a comma — this is a CLIENT-LOCAL storage
   * key only, never sent to the API, so it cannot corrupt the `apps` array of any record.
   */
  private get appStorageKey(): string {
    return this.appNames.join(',');
  }

  private get registeredAppTranslationKeys(): string[] {
    const storedTranslations = this.storage.registeredAppTranslations;
    return (storedTranslations && storedTranslations[this.appStorageKey]) || [];
  }

  /**
   * Get app text translations from the API as a map of key-value pairs, where the key is the translation key and the
   * value is the translated text in the requested language.
   *
   * When the config names multiple apps, each is fetched with its OWN single-app request and the
   * results are merged — no comma-joined `appName` is ever sent, so a write path can never receive a
   * composite value. Merge order follows the configured app order: later apps fill in keys the
   * earlier ones didn't provide, and never clobber an earlier app's non-empty value (landing and
   * web-traveler key sets don't overlap, so order is immaterial in practice, but it is deterministic).
   *
   * @param lang The language code for the translations to get
   * @returns An observable of the translation map
   * @throws Error if the API address is not set or if NO app text is received from ANY app
   */
  public getAppTranslations(lang: string): Observable<TranslationMap> {
    if (!this.config.apiAddress) {
      throw new Error('API address not set');
    }

    const requests = this.appNames.map((appName) => {
      const url = new URL(`${this.config.apiAddress}/app_text`);
      url.searchParams.append('appName', appName); // always ONE real app name — never a comma list
      url.searchParams.append('locale', lang);
      return this.http.get<TranslationMap>(url.toString());
    });

    return forkJoin(requests).pipe(
      map((results) => {
        // Merge earlier→later so a later app fills gaps without overwriting an earlier non-empty value.
        // A blank value ('') does NOT count as "set": a key seeded empty in an earlier app must not
        // shadow a real translation for the same key in a later app (it would silently drop the text).
        const merged: TranslationMap = {};
        for (const res of results) {
          for (const [key, value] of Object.entries(res)) {
            if (merged[key] === undefined || merged[key] === '') {
              merged[key] = value;
            }
          }
        }
        if (Object.keys(merged).length > 0) {
          return merged;
        }
        // Only fail when EVERY app returned empty — one empty app is fine if the union is non-empty.
        throw new Error('No app text received from API');
      }),
    );
  }

  /**
   * Exclude the given app translation keys from being registered with the API
   * @param keys The keys to exclude
   */
  public excludeAppTranslationKeys(...keys: string[]): void {
    keys.forEach((key) => this.excludedAppTranslationKeys.add(key));
  }

  /**
   * Register the given app translation keys with the API
   * @param keys The keys to register
   * @returns An observable that emits when the keys have been registered
   * @throws Error if the API address is not set
   */
  public registerAppTranslationKeys(keys: string[]): Observable<void> {
    if (!this.config.apiAddress) {
      throw new Error('API address not set');
    }
    if (this.config.registerKeys === false) {
      // Read-only surface (e.g. the prerendered landing app): never author keys at runtime.
      // Default (undefined/true) keeps registering as before.
      return of();
    }
    const url = new URL(`${this.config.apiAddress}/app_text`);
    // Register under a SINGLE real app name (the first configured) — never a comma-joined list, which
    // would corrupt the `apps` array. Multi-app configs are read-only (registerKeys:false) in practice,
    // so this path is only reached by single-app apps anyway.
    url.searchParams.append('appName', this.appNames[0]);

    // Filter out excluded keys and keys that were already submitted for the current app
    keys = keys.filter(
      (key) => !this.excludedAppTranslationKeys.has(key) && !this.registeredAppTranslationKeys.includes(key),
    );

    if (keys.length === 0) {
      // No translation keys to register
      return of();
    }

    // Save the keys we are submitting so we don't have to submit them again
    this.saveRegisteredAppTranslationKeys(keys);

    return this.http.post(url.toString(), keys, false);
  }

  private saveRegisteredAppTranslationKeys(newTranslationKeys: string[]) {
    // Get existing stored translations, if any
    let storedTranslations = this.storage.registeredAppTranslations;

    const storageKey = this.appStorageKey;
    if (!storedTranslations) {
      // No translation keys stored, create a new object
      storedTranslations = { [storageKey]: newTranslationKeys.sort() };
    } else if (!storedTranslations[storageKey]) {
      // No translation keys stored for the current app
      storedTranslations[storageKey] = newTranslationKeys.sort();
    } else {
      // Merge with existing keys stored for the current app
      const combinedKeys = new Set(storedTranslations[storageKey]);
      newTranslationKeys.forEach((key) => combinedKeys.add(key));
      storedTranslations[storageKey] = Array.from(combinedKeys).sort();
    }

    // Save the registered translations in browser storage
    this.storage.registeredAppTranslations = storedTranslations;
  }
}
